import React, { useEffect, useRef } from 'react';
import { throttle } from 'lodash';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Byline from '~components/base/Bylines';

const TopperWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  padding: 0 20px;
  margin: 2rem auto 8rem;
  &:after {
    display: table;
    content: '';
    clear: both;
  }
  @media (max-width: 700px) {
    margin: 2rem auto 4rem;
  }
`;

const TopperImageWrapper = styled.div`
  float: right;
  max-width: 700px;
  margin-left: 20px;
  @media(max-width: 1200px) {
    max-width: 600px;
  }
  @media(max-width: 1100px) {
    max-width: 600px;
    float: none;
    margin: 0 auto 3rem;
  }
  p {
    font-size: 0.75rem;
    line-height: 0.875rem;
  }
`;

const TopperImage = styled.div`
  padding: 10px;
  background-color: var(--custom-white);
  box-shadow: var(--customShadow);
  margin-bottom: 0.5rem;
  border-radius: 3px;
`;

const TopperCopy = styled.div`
  text-align: right;
  padding-right: 20px;
  width: calc(100% - 700px);
  h1, h2 {
    color: var(--custom-white);
  }
  h1 {
    padding-top: 4rem;
    font-size: 4.5rem;
    line-height: 4.75rem;
    margin-bottom: 1rem;
  }
  h2 {
    font-size: 1.875rem;
    line-height: 2.25rem;
    margin-bottom: 1rem;
    letter-spacing: 0.35px;
  }
  @media (max-width: 1200px) {
    width: calc(100% - 600px);
  }
  @media (max-width: 1100px) {
    max-width: 700px;
    width: 100%;
    text-align: left;
    margin: 0 auto;
    h1 {
      padding-top: 0;
      font-size: 4rem;
      line-height: 4.25rem;
    }
  }
  @media (max-width: 700px) {
    h1 {
      font-size: 3.5rem;
      line-height: 3.75rem;
    }
    h2 {
      font-size: 1.5rem;
      line-height: 1.625rem;
    }
  }
`;

const Topper = ({
  img,
  head,
  deck,
  byline,
  pubDate,
  setStickyView,
}) => {
  const HeroImageRef = useRef(null);
  useEffect(() => {
    function updateStickyView() {
      if (window.pageYOffset > 50) {
        setStickyView(true);
      } else {
        setStickyView(false);
      }
    }

    window.addEventListener('scroll', throttle(updateStickyView, 250));

    return (_) => {
      window.removeEventListener('scroll');
    };
  }, [setStickyView]);

  return (
    <TopperWrapper className="topper">
      <TopperImageWrapper>
        <TopperImage className="topper__image">
          <img
            className="hero__image"
            srcSet={img.srcSet}
            src={img.url}
            alt={img.caption}
            sizes="(max-width: 240px) 480vw, (max-width: 320px) 640vw, (max-width: 600px), 1024vw, 2048vw"
            ref={HeroImageRef}
          />
        </TopperImage>
        <p className="benton-n4">{img.caption}</p>
      </TopperImageWrapper>
      <TopperCopy className="topper__copy">
        <h1 className="millerbanner-n4">{head}</h1>
        <h2 className="millerbanner-n4">{deck}</h2>
        <Byline
          data={byline}
          pubDate={pubDate}
        />
      </TopperCopy>
    </TopperWrapper>
  );
};

export default Topper;

Topper.defaultProps = {
  pubDate: null,
};

Topper.propTypes = {
  img: PropTypes.shape().isRequired,
  head: PropTypes.string.isRequired,
  deck: PropTypes.string.isRequired,
  byline: PropTypes.arrayOf(
    PropTypes.shape(),
  ).isRequired,
  pubDate: PropTypes.shape(),
  setStickyView: PropTypes.func.isRequired,
};
